<template>
  <div>
    <div v-if="selectedDay !== null && numSlotsShowing === 0" class="text-center pt-2">
       No openings are available on this day.
    </div>
    <div class="slot-select-section">
      <div v-for="room in availRooms" :key="room" class="slot-column">
        <template v-for="(slot, slotIndex) in addBreaksToOpenings(studioSlots, room)">
          <div :key="slot.id">
             <div v-if="slotIndex === 0" class="instrumentHeader">{{ getInstrumentHeader(studioSlots.get(room)[0]) }}</div>
                <div
                   class="slot"
                   :class="getSlotClass(slot)"
                  @click="handleSlotClicked(slot)"
              >
                <span v-if="slot.status !== 'break'">
                {{ formatSlotText(slot) }}
                </span>
              </div>
          </div>

        </template>

      </div>

    </div>


  </div>
</template>

<script>

import {DateTime} from 'luxon'
import {computed, reactive, ref, toRefs} from "@vue/composition-api";
import {formatLocalTime, getScheduleLookup} from "@/model/scheduleAPI";

export default {
  name: "SlotSelect",
  props: {
    timeSlots: {type: Array},
    studioId: {type: String},
    selectedDay: {type: Object},
    student: {type: Object},
    selectedTimes: {type: Array},

  },
  setup(props, {emit}) {

    const {timeSlots, studioId, student, selectedTimes, selectedDay} = toRefs(props)

    const scheduleLookup = computed(() => getScheduleLookup(timeSlots.value));

    const studioSlots = computed(() => {
      if (selectedDay.value !== null) {
        return reactive(scheduleLookup.value.findOpenings(studioId.value, selectedDay.value));
      } else {
        return [];
      }
    });
    const availRooms = computed(() => {
      if (selectedDay.value !== null) {
        return reactive([...scheduleLookup.value.getRooms(studioId.value, selectedDay.value)])
      } else {
        return []
      }
    });

    const studentTime = ref([]);

    const numSlotsShowing = computed(function() {
      let numSlots = 0;
      for (const room of availRooms.value) {
        numSlots += studioSlots.value.get(room).filter(s => s.status === "available").length;
      }
      return numSlots;
    });

    function formatSlotTime(slot) {
      const studentOrNull = getSlotStudent(slot)
      if (slot.status === "unavailable") {
        return formatLocalTime(slot.date) //"";
      } else if (!doesInstrumentMatchSelectedStudent(slot)) {
        // Show instrument
        return 'Open for '+slot.instruments.map(s => s[0].toUpperCase() + s.substring(1)).join('/');
      }
      else if (studentOrNull === null && (!isSelectedStudentGradeAllowed(slot) || !isSelectedStudentCourseAllowed(slot))) {
        return "Restricted";
      }
      // } else if (!isSelectedStudentCourseAllowed(slot)) {
      //   return "Course"
      // }```
      return formatLocalTime(slot.date);
    }

    function doesInstrumentMatchSelectedStudent(slot) {
      if (!slot.instruments) {
        return true
      }
      return slot.instruments.includes(student.value.instrument.toLowerCase())
    }

    function isSelectedStudentGradeAllowed(slot) {
      if (!slot.grades) {
        return true;
      }
      const studentInstrument = student.value.instrument.toLowerCase()
      const minMaxGrades = slot.grades[studentInstrument]
      if (!minMaxGrades) {
        return true;
      }
      const [minGradeStr, maxGradeStr] = minMaxGrades;
      const minGrade = gradeStrToInt(minGradeStr)
      const maxGrade = gradeStrToInt(maxGradeStr)
      const studentGrade = gradeStrToInt(student.value.grade)
      const minConstraint = (minGrade === null || studentGrade >= minGrade)
      const maxConstraint = (maxGrade === null || studentGrade <= maxGrade)
      return minConstraint && maxConstraint;
    }

    function isSelectedStudentCourseAllowed(slot) {
      if (!slot.courses) {
        return true;
      }
      const studentInstrument = student.value.instrument.toLowerCase()
      const minMaxCourses = slot.courses[studentInstrument]
      if (!minMaxCourses) {
        return true;
      }
      const [minCourse, maxCourse] = minMaxCourses;

      const studentCourse = courseStrToInt(student.value.course)
      const minConstraint = (minCourse === null || studentCourse >= minCourse)
      const maxConstraint = (maxCourse === null || studentCourse <= maxCourse)
      return minConstraint && maxConstraint;
    }

    const gradeStrToInt = function(gradeStr) {
      if (gradeStr === null) {
        return null;
      }
      return {
        'pk': -1,
        'k': 0,
        '1': 1,
        '2': 2,
        '3': 3,
        '4': 4,
        '5': 5,
        '6': 6,
        '7': 7,
        '8': 8,
        '9': 9,
        '10': 10,
        '11': 11,
        '12': 12,
        'a': 13,
      }[gradeStr.toLowerCase()]
    }

    const courseStrToInt = function(courseStr) {
      if (courseStr === null) {
        return null;
      }
      if (courseStr.toLowerCase() === 'a') {
        return -2;
      } else if (courseStr.toLowerCase() === 'b') {
        return -1;
      } else if (courseStr.toLowerCase() === 'c') {
        return 0
      } else {
        return parseInt(courseStr)
      }
    }

    const isStudioDayEmpty = function(day) {
      return scheduleLookup.value.findOpenings(studioId.value, day).length === 0;
    }

    function addBreaksToOpenings(studioSlots, room) {

      // Find the room with the earliest time
      let earliestTime = null;
      studioSlots.forEach((slots) => {
        if (!earliestTime || DateTime.fromISO(slots[0].date) < earliestTime) {
          earliestTime = DateTime.fromISO(slots[0].date);
        }
      })
      if (earliestTime === null) {
        return [];
      }

      const outTimes = [];

      // Add breaks (empty space) at the top until we get to the first slot.
      let timeCounter = earliestTime;
      const openings = studioSlots.get(room);
      if (openings.length > 0) {
        const firstSlotTime = DateTime.fromISO(openings[0].date);
        while (timeCounter !== null &&
               timeCounter < firstSlotTime)  {
            outTimes.push({status: 'break'});
            timeCounter = timeCounter.plus({minutes: 5});
        }
      }

      // Add the openings and in-between breaks
      let prevOpeningTime = null;
      for (let opening of openings) {
        const openingTime = DateTime.fromISO(opening.date);
        // Add breaks in for every 5 minute gap
        while (prevOpeningTime !== null &&
               prevOpeningTime.plus({minutes: 30}) < openingTime)  {
            outTimes.push({status: 'break'});
            prevOpeningTime = prevOpeningTime.plus({minutes: 5});
        }
        outTimes.push(opening);
        prevOpeningTime = openingTime;
      }
      return outTimes;
    }

    function handleSlotClicked(slot) {
      if (slot.status === 'available' && !isSlotSelected(slot) &&
            doesInstrumentMatchSelectedStudent(slot) &&
            isSelectedStudentGradeAllowed(slot) &&
            isSelectedStudentCourseAllowed(slot)) {
        // Select as student's slot
        emit("student-selected-time", {slot, student: student.value});
      } else if (getSlotStudent(slot) === student.value) {
        emit("student-unselected-time", {slot, student: student.value});
      }
    }

    function isSlotSelected(slot) {
      //  check (>start date, =room, =dow, =time)
      //  so it shows selected each week  after the start date
      const matchingSelectedTime = findMatchingSelectedTime(slot);
      return matchingSelectedTime !== undefined;
    }

    function findMatchingSelectedTime(slot) {
      const slotDT = DateTime.fromISO(slot.date);
      return selectedTimes.value.find((time) => {

        const selectedDT = DateTime.fromISO(time.slot.date);
        return (selectedDT.weekday === slotDT.weekday
            && selectedDT.hour === slotDT.hour
            && selectedDT.minute === slotDT.minute
        && selectedDT <= slotDT
        && slot.roomID === time.slot.roomID);
      });
    }

    function getSlotStudent(slot) {
      // Returns null if not selected or the student if selected.
      const matchingSelectedTime = findMatchingSelectedTime(slot);
      if (matchingSelectedTime === undefined) {
        return null;
      } else {
        return matchingSelectedTime.student;
      }
    }

    function getSlotClass(slot) {
      let classes = {}

      const studentOrNull = getSlotStudent(slot);

      // Grade restrictions will take precedence over slot status
      if ((!isSelectedStudentGradeAllowed(slot) || !isSelectedStudentCourseAllowed(slot)) &&
            studentOrNull === null) {
        classes['restricted'] = true
      } else {
        // use available or unavailable status as a class
        classes[slot.status] = true;
      }

      if (!doesInstrumentMatchSelectedStudent(slot)) {
        classes['instrument-mismatch'] = true
      }

      if (studentOrNull === null) {
        classes['selected-other'] = false;
        classes['selected-self'] = false;
      } else {
        classes['selected-other'] = (studentOrNull.id !== student.value.id);
        classes['selected-self'] = (studentOrNull.id === student.value.id);
      }

      return classes
    }

    function formatSlotName(slot) {
      const studentOrNull = getSlotStudent(slot);
      if (studentOrNull === null) {
        return "";
      } else if (studentOrNull.id !== student.value.id) {
        return studentOrNull.firstName + ' (' + studentOrNull.instrument + ')'
      } else {
        return studentOrNull.firstName;
      }
    }

    function formatSlotText(slot) {
      if (getSlotStudent(slot) !== null && !doesInstrumentMatchSelectedStudent(slot)) {
        return formatSlotName(slot) // + ' (' + formatSlotTime(slot) + ')'
      }
      return formatSlotTime(slot) + ' ' + formatSlotName(slot)
    }

    function getInstrumentHeader(slot) {

      return `${slot.instruments.map((i) => i.charAt(0).toUpperCase() + i.slice(1)).join('/')}`
    }

    return {
      studioSlots,
      availRooms,
      numSlotsShowing,
      formatSlotText,
      formatSlotTime,
      formatSlotName,
      getSlotClass,
      isStudioDayEmpty,
      addBreaksToOpenings,

      handleSlotClicked,
      studentTime,
      getInstrumentHeader,
    }
  }
}
</script>

<style scoped>


.slot-select-section {
  margin-top: .5rem;

  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
}
.slot-column {
  /* Allow column to fill space */
  flex: 1 1 auto;
  padding: .25rem 0;
  margin: 0 .5rem;

  /* Flex container for slots in a column */
  display: flex;
  flex-flow: column nowrap;
}
.slot {
  flex: 1 0 auto;
  min-width: 75px;
  max-width: 100%;
  height: 30px;
  max-height: 30px;

  line-height: 1.8rem;
  margin-top: .2rem;
  text-align: center;
  font-size: .9rem;
  border: 1px solid #595959;
  border-radius: 15px;

  cursor: pointer;
  user-select: none;
}
.slot.unavailable {
  background-color: #EEEEEE;
  border-color: #EEEEEE;
  color: white;
  cursor: not-allowed;
}
.slot.available, .slot.taken {
  background-color: white;
  border-color: #595959;
  color: #595959;
}
.slot.restricted {
  background-color: white;
  border-color: #595959;
  color: #595959;
  cursor: not-allowed;
}
.slot.instrument-mismatch {
  cursor: not-allowed;
}
.slot.selected-self
 {
  background-color: #A72D32;
  color: white;
  font-weight: bold;
}
.slot.selected-other
 {
  border-color: #A72D32;
  color: #A72D32;
  font-weight: bold;
}
.slot.break {
  cursor: default;
  border: none;
  height: 5px;
  margin: 0;
}
.instrumentHeader {
  text-align: center;
  border: 1px solid #505050;
  font-size: 100%;
  font-weight: bold;
  text-transform: uppercase;
  padding: .5rem;
  margin-bottom: 1rem;
}

</style>